import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { CompanyLogo } from "@/components/CompanyLogo";
import {
  LayoutDashboard, Building2, GitBranch, Users, UserSquare2, DoorOpen,
  CalendarClock, BarChart3, ScrollText, History, Settings, Monitor,
  LogOut, Menu, X, ClipboardCheck, QrCode, ShieldCheck, Boxes, LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ROLE_LABELS = {
  SUPER_ADMIN: "Super Admin", COMPANY_ADMIN: "Company Admin",
  IT_ADMIN: "IT Admin", SERVICE_HEAD: "Service Head", SUPPORT_TEAM: "Support Team",
  BRANCH_ADMIN: "Branch Admin", RECEPTIONIST: "Receptionist",
};

// nav item: { to, label, icon, roles, feature }
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "SERVICE_HEAD", "SUPPORT_TEAM", "BRANCH_ADMIN", "RECEPTIONIST"] },
  { to: "/companies", label: "Companies", icon: Building2, roles: ["SUPER_ADMIN"] },
  { to: "/branches", label: "Branches", icon: GitBranch, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN"] },
  { to: "/users", label: "Users", icon: Users, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "BRANCH_ADMIN"] },
  { to: "/cabins", label: "Cabins", icon: Boxes, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "BRANCH_ADMIN"] },
  { to: "/tickets", label: "Ticketing", icon: LifeBuoy, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "SERVICE_HEAD", "SUPPORT_TEAM", "BRANCH_ADMIN", "RECEPTIONIST"] },
  { to: "/ticket-config", label: "Ticket Config", icon: Settings, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "SERVICE_HEAD"] },
  { to: "/visitors", label: "Visitor Management", icon: ClipboardCheck, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "BRANCH_ADMIN", "RECEPTIONIST"], feature: "visitor_management" },
  { to: "/rooms", label: "Meeting Rooms", icon: DoorOpen, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "BRANCH_ADMIN", "RECEPTIONIST"], feature: "meeting_room_booking" },
  { to: "/bookings", label: "Bookings", icon: CalendarClock, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "BRANCH_ADMIN", "RECEPTIONIST"], feature: "meeting_room_booking" },
  { to: "/history", label: "Visitor History", icon: History, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "BRANCH_ADMIN"], feature: "visitor_history" },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "SERVICE_HEAD", "BRANCH_ADMIN", "RECEPTIONIST"], feature: "reports" },
  { to: "/audit", label: "Audit Logs", icon: ScrollText, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "BRANCH_ADMIN"], feature: "audit_logs" },
  { to: "/wall", label: "Wall Display", icon: Monitor, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "BRANCH_ADMIN"], feature: "wall_display" },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["SUPER_ADMIN", "COMPANY_ADMIN", "IT_ADMIN", "BRANCH_ADMIN"] },
];

export default function AppLayout() {
  const { user, company, branch, features, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user.role))
    .filter((n) => !n.feature || user.role === "SUPER_ADMIN" || features[n.feature] !== false);

  const scopeLabel = branch?.name ? `${company?.name} · ${branch.name}` : company?.name || "Platform-wide access";

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-white/10">
        <Logo className="h-7" light />
        <p className="text-[11px] text-blue-200/70 mt-2 font-medium tracking-wide">WORKSPACE INTELLIGENCE</p>
      </div>
      <nav className="flex-1 overflow-y-auto sf-scroll px-3 py-4 space-y-1">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive ? "bg-white text-navy-900 shadow-sm" : "text-blue-100/80 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <n.icon size={18} strokeWidth={2} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/10">
        <div className="px-3 py-2 rounded-xl bg-white/5 mb-2">
          <p className="text-white text-sm font-semibold truncate">{user.name}</p>
          <p className="text-[11px] text-blue-200/70 flex items-center gap-1">
            <ShieldCheck size={12} /> {ROLE_LABELS[user.role]}
          </p>
        </div>
        <button
          onClick={() => { logout(); navigate("/login"); }}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-blue-100/80 hover:bg-rose-500/20 hover:text-white transition-colors"
        >
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 bg-navy-900 flex-col fixed inset-y-0 z-30">
        <SidebarContent />
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-navy-900"><SidebarContent /></aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-20 px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-navy-900" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div className="min-w-0">
              <p className="text-xs text-slate-400 font-medium">{ROLE_LABELS[user.role]}</p>
              <p className="text-sm font-semibold text-navy-900 truncate flex items-center gap-1.5">
                <QrCode size={14} className="text-blue-600" /> {scopeLabel}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {company?.logo_url && <CompanyLogo logoUrl={company.logo_url} className="h-8 hidden sm:flex" alt={company?.name} />}
            <div className="w-9 h-9 rounded-full bg-navy-900 text-white flex items-center justify-center font-display font-bold text-sm">
              {user.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
